/**
 * API client per la chat.
 */

const API_BASE = '/api/v1';

export interface SuggestedAction {
  label: string;
  type: 'prompt' | 'action';
  prompt?: string;
  action?: string;
  email_id?: string;
}

export interface ChatRequest {
  message: string;
  scope: 'global' | 'thread';
  case_id?: string;
  history: Array<{ role: string; content: string }>;
}

export interface ChatResponse {
  response: string;
  thinking?: string;
  source_email_ids: string[];
  suggested_actions?: SuggestedAction[];
  scope: 'global' | 'thread';
}

export interface StreamChunk {
  chunk?: string;
  done?: boolean;
  thinking?: string;
  source_email_ids?: string[];
  suggested_actions?: SuggestedAction[];
  error?: string;
}

export const chatApi = {
  async send(request: ChatRequest): Promise<ChatResponse> {
    const response = await fetch(`${API_BASE}/chat/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async stream(
    request: ChatRequest,
    onChunk: (chunk: StreamChunk) => void
  ): Promise<ChatResponse> {
    const response = await fetch(`${API_BASE}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let fullResponse = '';
    let thinking: string | undefined;
    let source_email_ids: string[] = [];

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            try {
              const chunk: StreamChunk = JSON.parse(jsonStr);
              onChunk(chunk);

              if (chunk.chunk) {
                fullResponse += chunk.chunk;
              }
              if (chunk.done) {
                thinking = chunk.thinking;
                source_email_ids = chunk.source_email_ids || [];
              }
              if (chunk.error) {
                throw new Error(chunk.error);
              }
            } catch (parseError) {
              if (jsonStr && process.env.NODE_ENV === 'development') {
                console.debug('[SSE] Skipped invalid JSON:', jsonStr.slice(0, 50));
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return {
      response: fullResponse,
      thinking,
      source_email_ids,
      scope: request.scope,
    };
  },
};
